import { BrandLogo } from '@cibo/profile'
import { FriendlyError, Scrim, useFigures } from '@cibo/ui'
import ArrowForward from '@mui/icons-material/ArrowForward'
import {
  Box,
  CardContent,
  CardMedia,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { UserProgramCardDisplayProps } from '../UserProgramCardTypes'
import { UserProgramCardBanner } from './UserProgramCardBanner'

export const UserProgramCardDisplay = ({
  program,
  isLoading,
  isError,
  orgContent,
  userProgramData,
}: UserProgramCardDisplayProps) => {
  const { t } = useTranslation('impact/UserProgramsCard')
  const { acres } = useFigures()

  return (
    <>
      {isError ? (
        <FriendlyError message={t('couldNotLoadProgram')} />
      ) : (
        <>
          <CardMedia
            image={
              program?.content.fields.promoImages
                ? `${program?.content.fields.promoImages?.[0].fields.file.url}?h=500&fm=jpg&q=50`
                : undefined
            }
            sx={{ position: 'relative' }}
          >
            <Scrim />

            <Box zIndex={1} position="relative" display="flex" flexDirection="column">
              <Box>
                <UserProgramCardBanner
                  userProgramStatus={userProgramData.cardStatus}
                  engageType={program?.engageType}
                />
              </Box>

              <Stack spacing={1} p={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="primary.contrastText" variant="h5">
                    {isLoading ? <Skeleton /> : program?.content.fields.title}
                  </Typography>
                  <ArrowForward sx={{ color: 'primary.contrastText' }} />
                </Stack>
                <Typography color="primary.contrastText">
                  {isLoading ? <Skeleton /> : program?.content.fields.shortDescription}
                </Typography>
              </Stack>
            </Box>
          </CardMedia>

          <CardContent sx={{ flex: 1, display: 'flex' }}>
            <Stack spacing={2} flex={1}>
              {!!program?.syndicating && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography color="text.secondary" variant="overline">
                    {t('sponsored by')}
                  </Typography>
                  <BrandLogo
                    altText={`${t('sponsored by')} ${orgContent?.fields.name}`}
                    size="small"
                    variant="square"
                    contentfulOrgId={program.sponsorOrg?.contentfulOrgId}
                  />
                </Stack>
              )}

              <Grid columns={{ xs: 12 }} container>
                <Grid key="eligible" xs={4} item>
                  <Typography color="text.secondary" variant="overline">
                    {t('Eligible')}
                  </Typography>
                  <Typography>{acres(userProgramData.eligibleForEnrollment ?? 0)}</Typography>
                </Grid>
                <Grid key="enrollments" xs={4} item>
                  {!!userProgramData.currentStepType ? (
                    <>
                      <Typography color="text.secondary" variant="overline">
                        {t('Application Status')}
                      </Typography>
                      <Typography>{t(`stepType_${userProgramData.currentStepType}`)}</Typography>
                    </>
                  ) : (
                    <>
                      <Typography color="text.secondary" variant="overline">
                        {t('Enrollments')}
                      </Typography>
                      <Typography>{acres(userProgramData.enrolling ?? 0)}</Typography>
                    </>
                  )}
                </Grid>
                <Grid key="enrolled" xs={4} item>
                  <Typography color="text.secondary" variant="overline">
                    {t('Enrolled')}
                  </Typography>
                  <Typography>{acres(userProgramData.enrolled ?? 0)}</Typography>
                </Grid>
              </Grid>

              <Divider />

              <Typography color="text.secondary" variant="caption">
                {t('possibleEarningsDescription')}
              </Typography>
            </Stack>
          </CardContent>
        </>
      )}
    </>
  )
}
